/*
 *
 * DisputeFilterModal
 *
 * @version 1.0.0
 * @author Alessandro Mancuso
 * @since 15-May-2025
 */

import React, { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'

// Components
import { Eraser } from '@nexidigital/iconlibrary'
import Toggle from 'Hoc/ToggleComponent'
import MerchantModal from '@nexidigital/merchantmodal'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import PortalRadioBox from '@nexidigital/portalradiobox'
// import PortalSelect from '@nexidigital/portalselect'
import PortalSelect from '@nexidigital/portalselect'
import DatePicker from '@nexidigital/datepicker'
import PortalTextInput from '@nexidigital/portaltextinput'

// Utilities
import { FORM_KEYS, getValidationSchema, getProvinceOptionsByRegion, mapInitialFiltersToFormikValues, mapFormikValuesToFilteredRequestParams, normalizeProvinceForRegion } from './DisputeFilterModal.utils'
import { useFormik } from 'formik'
import { useMobileDetect } from 'Hoc/ListenerProvider'
import { useIntl } from 'react-intl'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { mapStatus } from '../../modules/disputeStatusMapper'
import { ensureDefaultFiltersForTab, getDefaultFiltersForTab } from '../../modules/disputeTabFilters'

// Texts
import formattedMessage from './languageModule'

// Styles
import './DisputeFilterModal.scss'
import GroupedRadioSelector from 'Components/GroupedRadioSelector'

const DisputeFilterModal = (props) => {
  const intl = useIntl()
  const { isMobile } = useMobileDetect()

  const initialFilters = ensureDefaultFiltersForTab(props.disputeTab, props.appliedFilters || {}, FORM_KEYS.PRACTICAL_STATUS)

  // Initial values for Formik
  const initialValues = mapInitialFiltersToFormikValues(initialFilters, FORM_KEYS)
  // Validation schema for Formik
  const validationSchema = getValidationSchema(FORM_KEYS)

  // Capture the values as they were when the modal was opened — never changes during the modal lifecycle
  const valuesAtOpen = useRef(initialValues)

  const defaultFormValues = useMemo(
    () => mapInitialFiltersToFormikValues(getDefaultFiltersForTab(props.disputeTab, FORM_KEYS.PRACTICAL_STATUS), FORM_KEYS),
    [props.disputeTab]
  )

  // State to manage if apply filter button is enabled or not
  const [isFormEdited, setIsFormEdited] = useState(false)

  // State to manage if Reset filters is clicked
  const [isFiltersReset, setIsFiltersReset] = useState(false)

  // State to force re-render of PortalRadioBox component
  const [periodRadioKey, setPeriodRadioKey] = useState(0)
  const [practicalStatusRadioKey, setPracticalStatusRadioKey] = useState(0)
  const [researchTypeRadioKey, setResearchTypeRadioKey] = useState(0)

  // State to manage selected chip
  // const [selectedChip, setSelectedChip] = useState(null)

  // Props handlers
  const onResetFilters = props.onResetFilters

  // const defaultFormValues = useMemo(() => ({
  //   [FORM_KEYS.PERIOD]: '',
  //   [FORM_KEYS.REGION]: '',
  //   [FORM_KEYS.PROVINCE]: ''
  // }), [])

  // Formik setup
  const formik = useFormik({
    // Set ALL default initialValues for Formik to corret set isDirty and isValid on initial modal render
    // to set apply button disabled state correctly
    initialValues,
    // Used to disable apply button if no changes are made
    validate: values => {
      if (isEqual(values, valuesAtOpen.current)) {
        setIsFormEdited(false)
      } else {
        setIsFormEdited(true)
      }
    },
    validationSchema,
    onSubmit: values => {
      const normalizedValues = ensureDefaultFiltersForTab(props.disputeTab, values, FORM_KEYS.PRACTICAL_STATUS)
      const filterParams = mapFormikValuesToFilteredRequestParams(normalizedValues, FORM_KEYS)

      const TAB_ACTION_MAP = {
        PENDING: props.actions.doGetDisputesPending,
        PROCESSING: props.actions.doGetDisputesInProgress,
        CLOSED: props.actions.doGetDisputesClosed
      }
      const action = TAB_ACTION_MAP[props.disputeTab]
      if (action) action({ ...filterParams, sendAnalytics: true })

      props.actions.doSetDisputeFilters({ tab: props.disputeTab, filters: normalizedValues })
      props.actions.doHidePopup()
    }
  })

  const practicalStatusOptions = useMemo(() => {
    const toOption = (id) => ({
      id,
      label: mapStatus(id, intl),
      value: id
    })
    if (props.disputeTab === 'CLOSED') {
      return ['EVASED_IN_TIMES', 'EVASED_AFTER_EXPIRY'].map(toOption)
    }
    return ['NEW', 'EXPIRING', 'EXPIRED'].map(toOption)
  }, [props.disputeTab, intl])

  const hasActiveFilters = useMemo(() => !isEqual(formik.values, defaultFormValues), [defaultFormValues, formik.values])

  const filteredProvinceOptions = useMemo(
    () => getProvinceOptionsByRegion(
      formik.values[FORM_KEYS.REGION],
      props.provinceOptions,
      props.regionProvinceOptions
    ),
    [formik.values, props.provinceOptions, props.regionProvinceOptions]
  )

  useEffect(() => {
    if (isFiltersReset) return

    // Error management
    console.log('Formik errors:', formik.errors)
  }, [formik.errors])

  const periodFilterOptions = [
    {
      id: 'LAST_13_MONTHS',
      label: intl.formatMessage({ id: 't.app.disputeUci.filtersModal.period.last13Months', defaultMessage: 'Ultimi 13 mesi' }),
      value: 'LAST_13_MONTHS'
    },
    {
      id: 'PERSONALIZED',
      label: intl.formatMessage({ id: 't.app.disputeUci.filtersModal.period.personalized', defaultMessage: 'Periodo personalizzato' }),
      value: 'PERSONALIZED',
      children: (
        <div>
          <DatePicker
            id='filtersModalDatePickerPersonalizedPeriod'
            isRange
            id1='filtersModalDatePickerPersonalizedPeriod1'
            label1={formattedMessage.datePickerLabelFrom}
            name1={FORM_KEYS.PERSONALIZED_PERIOD_FROM}
            placeholder1={intl.formatMessage({ id: 't.app.datePicker.placeholder.default', defaultMessage: 'gg/mm/aaaa' })}
            id2='filtersModalDatePickerPersonalizedPeriod2'
            label2={formattedMessage.datePickerLabelTo}
            name2={FORM_KEYS.PERSONALIZED_PERIOD_TO}
            value1={formik.values[FORM_KEYS.PERSONALIZED_PERIOD_FROM]}
            value2={formik.values[FORM_KEYS.PERSONALIZED_PERIOD_TO]}
            onChange1={(date) => handleDateChange(FORM_KEYS.PERSONALIZED_PERIOD_FROM, date)}
            onChange2={(date) => handleDateChange(FORM_KEYS.PERSONALIZED_PERIOD_TO, date)}
            minDate={moment().subtract(13, 'months').add(1, 'day')}
            maxDate={moment()}
            error1={formik.touched[FORM_KEYS.PERSONALIZED_PERIOD_FROM] && formik.errors[FORM_KEYS.PERSONALIZED_PERIOD_FROM]}
            errorMessage1={formik.touched[FORM_KEYS.PERSONALIZED_PERIOD_FROM] && formik.errors[FORM_KEYS.PERSONALIZED_PERIOD_FROM]}
            error2={formik.touched[FORM_KEYS.PERSONALIZED_PERIOD_TO] && formik.errors[FORM_KEYS.PERSONALIZED_PERIOD_TO]}
            errorMessage2={formik.touched[FORM_KEYS.PERSONALIZED_PERIOD_TO] && formik.errors[FORM_KEYS.PERSONALIZED_PERIOD_TO]}
            placeholder2={intl.formatMessage({ id: 't.app.datePicker.placeholder.default', defaultMessage: 'gg/mm/aaaa' })}
            onBlur1={(value) => handleDateBlur(FORM_KEYS.PERSONALIZED_PERIOD_FROM, value)}
            onBlur2={(value) => handleDateBlur(FORM_KEYS.PERSONALIZED_PERIOD_TO, value)}
            isMobile={isMobile}
            labelYear={intl.formatMessage({ id: 't.app.year' })}
            labelMonth={intl.formatMessage({ id: 't.app.month' })}
            locale='it'
            labelsLocale={intl.locale}
          />
        </div>
      )
    }
  ]

  const researchTypeOptions = [
    {
      label: intl.formatMessage({ id: 't.app.disputeUci.filtersModal.researchType.byCode', defaultMessage: 'Codice pratica' }),
      value: 'RESEARCH_BY_CODE',
      children: (
        <div className='filterPopupResearchInput'>
          <PortalTextInput
            id={FORM_KEYS.RESEARCH_BY_CODE}
            name={FORM_KEYS.RESEARCH_BY_CODE}
            label={formattedMessage.fieldResearchByCode}
            placeholder={intl.formatMessage({ id: 't.app.disputeUci.filtersModal.researchType.byCode.placeholder', defaultMessage: 'Inserisci codice pratica' })}
            {...formik.getFieldProps(FORM_KEYS.RESEARCH_BY_CODE)}
            error={formik.touched[FORM_KEYS.RESEARCH_BY_CODE] && formik.errors[FORM_KEYS.RESEARCH_BY_CODE]}
            errorText={formik.touched[FORM_KEYS.RESEARCH_BY_CODE] && formik.errors[FORM_KEYS.RESEARCH_BY_CODE]}
            cleanField
          />
        </div>
      )
    },
    {
      label: intl.formatMessage({ id: 't.app.disputeUci.filtersModal.researchType.byOrder', defaultMessage: 'Numero ordine transazione' }),
      value: 'RESEARCH_BY_ORDER',
      children: (
        <div className='filterPopupResearchInput'>
          <PortalTextInput
            id={FORM_KEYS.RESEARCH_BY_ORDER}
            name={FORM_KEYS.RESEARCH_BY_ORDER}
            label={formattedMessage.fieldResearchByOrder}
            placeholder={intl.formatMessage({ id: 't.app.disputeUci.filtersModal.researchType.byOrder.placeholder', defaultMessage: 'Inserisci il numero ordine transazione' })}
            {...formik.getFieldProps(FORM_KEYS.RESEARCH_BY_ORDER)}
            error={formik.touched[FORM_KEYS.RESEARCH_BY_ORDER] && formik.errors[FORM_KEYS.RESEARCH_BY_ORDER]}
            errorText={formik.touched[FORM_KEYS.RESEARCH_BY_ORDER] && formik.errors[FORM_KEYS.RESEARCH_BY_ORDER]}
            cleanField
          />
        </div>
      )
    }
  ]

  // PERIOD SECTION - Handle startDate and endDate changes
  const handleDateChange = (field, date) => {
    formik.setFieldValue(field, date)
    // setSelectedChip(null)
  }

  const handleDateBlur = (field, date) => {
    formik.setFieldValue(field, date, true)
    formik.setFieldTouched(field, true, false)
  }

  const handleChange = (field, value) => {
    formik.setFieldValue(field, value)
  }

  const handleRegionChange = (value) => {
    const regionCode = value || ''
    const normalizedProvince = normalizeProvinceForRegion(
      regionCode,
      formik.values[FORM_KEYS.PROVINCE],
      props.provinceOptions,
      props.regionProvinceOptions
    )

    formik.setFieldValue(FORM_KEYS.REGION, regionCode)
    if (normalizedProvince !== formik.values[FORM_KEYS.PROVINCE]) {
      formik.setFieldValue(FORM_KEYS.PROVINCE, normalizedProvince)
    }
  }

  const handleResetFilters = () => {
    // Reset local states
    // setSelectedChip(PERIOD_CHIP_OPTIONS.LAST_7_DAYS.id)
    setIsFormEdited(false)
    setIsFiltersReset(true)

    // Reset Formik values to default
    formik.resetForm({
      values: defaultFormValues,
      // Reset all touched fields
      touched: {},
      // Reset all errors
      errors: {}
    })

    // Force PortalRadioBox rerender
    setPeriodRadioKey(prev => prev + 1)
    setPracticalStatusRadioKey(prev => prev + 1)
    setResearchTypeRadioKey(prev => prev + 1)

    // Force validation after reset
    setTimeout(() => {
      setIsFiltersReset(false)
      formik.validateForm()
    }, 0)

    if (onResetFilters) {
      onResetFilters()
    }
  }

  return (
    <Toggle>
      <MerchantModal
        iconName='errorCheck'
        onClose={() => props.actions.doHidePopup()}
        topBarTitle={formattedMessage.title}
        primaryAction={formik.handleSubmit} // eslint-disable-line
        primaryButtonLabel={formattedMessage.applyFilters}
        primaryButtonDisabled={!formik.isValid || !isFormEdited || formik.isSubmitting}
        idResetFilterCta='resetFilter'
        resetFilterLabel={formattedMessage.resetFilterLabel}
        onResetFilter={() => {
          handleResetFilters()
        }}
        disabledResetFilter={(!hasActiveFilters && !isFormEdited) || formik.isSubmitting}
        iconReset={<Eraser size='24' type='line' />}
      >
        <div className='filterPopupContent'>
          <MerchantGenericCard className='genericCardContent' empty>
            <div>
              <h2 className='filterPopupTitle'>
                {formattedMessage.titleSectionPeriod}
              </h2>
              <p className='filterPopupSubtitle'>
                {formattedMessage.periodHelper}
              </p>
              <div className='filterPopupPeriodRadioGroup'>
                <PortalRadioBox
                  key={periodRadioKey} // Force re-render when reset to clear selection
                  radioGroupName='periodFilterGroup'
                  name={FORM_KEYS.PERIOD}
                  value={formik.values[FORM_KEYS.PERIOD]}
                  defaultValue={formik.values[FORM_KEYS.PERIOD] || false}
                  onChange={value => formik.setFieldValue(FORM_KEYS.PERIOD, value)}
                  options={periodFilterOptions}
                  noPreselection
                />
              </div>
            </div>
            {/* <div> */}
            <div>
              <h2 className='filterPopupTitle'>
                {formattedMessage.titleSectionGeoArea}
              </h2>
              <PortalSelect
                id={FORM_KEYS.REGION}
                name={FORM_KEYS.REGION}
                label={formattedMessage.fieldRegion}
                options={props.regionOptions}
                placeholder={intl.formatMessage({ id: 't.app.disputeUci.filter.field.region.placeholder', defaultMessage: 'Seleziona regione' })}
                {...formik.getFieldProps(FORM_KEYS.REGION)}
                onChange={(event) => handleRegionChange(event?.value)}
                error={formik.touched[FORM_KEYS.REGION] && formik.errors[FORM_KEYS.REGION]}
                errorText={formik.touched[FORM_KEYS.REGION] && formik.errors[FORM_KEYS.REGION]}
              />
              <PortalSelect
                id={FORM_KEYS.PROVINCE}
                name={FORM_KEYS.PROVINCE}
                label={formattedMessage.fieldProvince}
                options={filteredProvinceOptions}
                placeholder={intl.formatMessage({ id: 't.app.disputeUci.filter.field.province.placeholder', defaultMessage: 'Seleziona provincia' })}
                {...formik.getFieldProps(FORM_KEYS.PROVINCE)}
                onChange={(event) => handleChange(FORM_KEYS.PROVINCE, event?.value || '')}
                error={formik.touched[FORM_KEYS.PROVINCE] && formik.errors[FORM_KEYS.PROVINCE]}
                errorText={formik.touched[FORM_KEYS.PROVINCE] && formik.errors[FORM_KEYS.PROVINCE]}
              />
            </div>
            <div>
              <h2 className='filterPopupTitle'>
                {formattedMessage.titleSectionData}
              </h2>
              {props.terminals && props.terminals.length > 0 && (
                <div className='filterPopupTerminalSelector'>
                  <GroupedRadioSelector
                    groupedOptions={{ terminalList: props.terminals }}
                    optionsGroupedLabels={{ terminalList: '' }}
                    selectedOption={formik.values[FORM_KEYS.TERMINAL_ID]}
                    onChangeSelectedOption={(selected) => formik.setFieldValue(FORM_KEYS.TERMINAL_ID, selected)}
                    selectPlaceholder={formattedMessage.selectTerminalIdPlaceholder}
                    selectLabel={formattedMessage.terminalIdLabel}
                    selectAllLabel={formattedMessage.selectAll}
                    searchPlaceholder={formattedMessage.searchbarPlaceholder}
                    noResultsLabel={formattedMessage.noResults}
                  />
                </div>
              )}

              {props.disputeTab !== 'PROCESSING' && (
                <div className='filterPopupCheckboxes'>
                  <span className='checkboxGrouplabel'>
                    {formattedMessage.fieldPracticalStatus}
                  </span>
                  <div className='filterPopupPracticalStatusRadioGroup'>
                    <PortalRadioBox
                      key={practicalStatusRadioKey}
                      radioGroupName='practicalStatusFilterGroup'
                      name={FORM_KEYS.PRACTICAL_STATUS}
                      value={formik.values[FORM_KEYS.PRACTICAL_STATUS]?.[0] || false}
                      defaultValue={formik.values[FORM_KEYS.PRACTICAL_STATUS]?.[0] || false}
                      onChange={value => formik.setFieldValue(FORM_KEYS.PRACTICAL_STATUS, value ? [value] : [])}
                      options={practicalStatusOptions}
                    />
                  </div>
                </div>
              )}
            </div>
            <div>
              <h2 className='filterPopupTitle'>
                {formattedMessage.titleSectionResearch}
              </h2>
              <div className='filterPopupResearchRadioGroup'>
                <PortalRadioBox
                  key={researchTypeRadioKey} // Force re-render when reset to clear selection
                  radioGroupName='researchTypeFilterGroup'
                  name={FORM_KEYS.RESEARCH_TYPE}
                  value={formik.values[FORM_KEYS.RESEARCH_TYPE]}
                  defaultValue={formik.values[FORM_KEYS.RESEARCH_TYPE] || false}
                  onChange={value => formik.setFieldValue(FORM_KEYS.RESEARCH_TYPE, value)}
                  options={researchTypeOptions}
                  noPreselection
                />
              </div>
            </div>
          </MerchantGenericCard>
        </div>
      </MerchantModal>
    </Toggle>
  )
}

DisputeFilterModal.propTypes = {
  actions: PropTypes.shape({
    doSetDisputeFilters: PropTypes.func.isRequired,
    doGetDisputesPending: PropTypes.func.isRequired,
    doGetDisputesInProgress: PropTypes.func.isRequired,
    doGetDisputesClosed: PropTypes.func.isRequired,
    doHidePopup: PropTypes.func.isRequired
  }).isRequired,
  disputeTab: PropTypes.string.isRequired,
  regionOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })),
  provinceOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })),
  regionProvinceOptions: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    provinces: PropTypes.array
  })),
  terminalIdOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })),
  practicalStatusOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })),
  terminals: PropTypes.array,
  onResetFilters: PropTypes.func,
  appliedFilters: PropTypes.object
}

DisputeFilterModal.defaultProps = {
  actions: {
    doHidePopup: () => { },
    doSetDisputeFilters: () => { },
    doGetDisputesPending: () => { },
    doGetDisputesInProgress: () => { },
    doGetDisputesClosed: () => { }
  },
  disputeTab: 'PENDING',
  regionOptions: [],
  provinceOptions: [],
  regionProvinceOptions: [],
  terminals: [],
  practicalStatusOptions: [],
  onResetFilters: () => { },
  appliedFilters: {}
}

export default DisputeFilterModal
